import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { LabeledInputComponent } from "../../../../../shared/components/labeled-input/labeled-input.component";
import { TextareaInputComponent } from "../../../../../shared/components/textarea-input/textarea-input.component";
import { CustomButtonComponent } from "../../../../../shared/components/custom-button/custom-button.component";
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { CommonValidatorService } from '../../../../../core/services/validators/common-validator.service';
import { ApiErrorService } from '../../../../../core/api/api-error.service';
import { SalesEngineService } from '../../../../../core/api/sales-engine.service';
import { OpportunityGet, OpportunityUpdate, SignalGet } from '../../../../../core/interfaces/sales-engine-interfaces';
import { ToastComponent } from '../../../../../shared/components/toast/toast.component';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomDropdownComponent } from "../../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { SalesEngineDataService } from '../../../../../core/services/sales-engine-data.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-create-edit-opportunity',
  standalone: true,
  imports: [LabeledInputComponent, TextareaInputComponent, CustomButtonComponent, CommonModule, TranslocoPipe, CustomDropdownComponent, FormsModule],
  templateUrl: './create-edit-opportunity.component.html',
  styleUrl: './create-edit-opportunity.component.css'
})
export class CreateEditOpportunityComponent {
  constructor(private commonValidator: CommonValidatorService, private apiError: ApiErrorService, private salesEngine: SalesEngineService, private salesEngineData: SalesEngineDataService) { }

  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();

  @Input() isEditing: boolean = false;
  @Input() opportunity?: OpportunityGet;

  @Input() toast?: ToastComponent;
  @Input() reloadCallbackFunction?: () => void;

  // Inputs
  nameInput: string = '';
  nameInputError: string | null = null;

  productsDropdownOptions: string[] = [];
  selectedProductDropdown?: number | undefined;

  descriptionInput: string = '';
  descriptionInputError: string | null = null;

  salesPromptInput: string = '';
  salesPromptInputError: string | null = null;

  signal_ids: string[] = []
  showPicker = false;

  // Button click
  confirmClicked: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    this.resetData();
    this.populateProductsDropdown();
    if (this.opportunity != undefined) {
      this.populateInputsFromOpportunity(this.opportunity)
    }
  }

  closeView() {
    this.isOpen = false;
    this.isOpenChange.emit(this.isOpen);
  }

  populateProductsDropdown(): void {
    this.productsDropdownOptions = [];
    this.selectedProductDropdown = undefined;

    this.salesEngineData.getProducts().forEach((product, index) => {
      this.productsDropdownOptions.push(product.name);

      if (this.isEditing) {
        if (product.id === this.opportunity?.product.id) {
          this.selectedProductDropdown = index;
        }
      }
    });
  }

  populateInputsFromOpportunity(opportunity: OpportunityGet) {
    this.nameInput = opportunity.name || '';
    this.descriptionInput = opportunity.description || '';
    this.salesPromptInput = opportunity.sales_prompt || '';

    for (let signal of opportunity.signals) {
      this.pickSignal(signal);
    }
  }

  resetData() {
    this.nameInput = this.descriptionInput = this.salesPromptInput = '';
    this.nameInputError = this.descriptionInputError = this.salesPromptInputError = null;
    this.signal_ids = [];
  }

  validateInputs(): boolean {
    const nameInputValue = this.nameInput.trim();
    const descriptionInputValue = this.descriptionInput.trim();

    // Validate length for text inputs
    const nameError = this.commonValidator.validateLength(nameInputValue, 2, 255);
    const descriptionError = this.commonValidator.validateLength(descriptionInputValue, 2, 255, false);

    // Show errors
    this.nameInputError = nameError;
    this.descriptionInputError = descriptionError;

    // If there are any errors show them and prevent submission
    if (nameError || descriptionError) {
      return false;
    }

    return true;
  }

  getOpportunityFromInputs(id: string = ''): OpportunityUpdate {
    const selectedProduct = this.salesEngineData.getProducts()[this.selectedProductDropdown!] || null;
    const result: OpportunityUpdate = {
      name: this.nameInput.trim(),
      description: this.descriptionInput,
      sales_prompt: this.salesPromptInput,
      product_id: selectedProduct.id,
      signal_ids: this.signal_ids
    }
    return result;
  }

  async confirmButtonClick() {
    if (this.confirmClicked) {
      return;
    }
    this.confirmClicked = true;

    if (!this.validateInputs()) {
      this.confirmClicked = false;
      return;
    }

    // Send request for creating location
    try {
      if (this.opportunity) {
        const newData = this.getOpportunityFromInputs(this.opportunity.id);
        await firstValueFrom(this.salesEngine.updateOpportunity(this.opportunity.id, newData));
      }
      else {
        const newData = this.getOpportunityFromInputs();
        await firstValueFrom(this.salesEngine.createOpportunity(newData));
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.confirmClicked = false;
      return;
    }

    this.confirmClicked = false;
    this.isOpen = false;

    if (this.reloadCallbackFunction) {
      this.reloadCallbackFunction();
    }
  }

  get signals(): SignalGet[] {
    return this.salesEngineData.getSignals();
  }

  // selected (resolved from ids, keeps display order by selection)
  get selectedSignals(): SignalGet[] {
    return this.signal_ids
      .map(id => this.signals.find(s => s.id === id))
      .filter((s): s is SignalGet => !!s);
  }

  // available for picker (exclude already selected)
  get availableSignals(): SignalGet[] {
    return this.signals.filter(s => !this.signal_ids.includes(s.id));
  }

  openPicker(): void {
    this.showPicker = true;
  }

  cancelPicker(): void {
    this.showPicker = false;
  }

  pickSignal(sig: SignalGet): void {
    if (!this.signal_ids.includes(sig.id)) {
      this.signal_ids.push(sig.id);
    }
    this.showPicker = false;
  }

  removeSignal(id: string): void {
    this.signal_ids = this.signal_ids.filter(sid => sid !== id);
  }
}
